import { AddIcon, Button, LessonIllustration, Table, TableColumns } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { useDeputateLessons } from '../../hooks/useDeputateLessons';
import styles from '../../LessonsComponents.module.scss';
import { LessonTableType } from '../../TimetableVersion/Table/TimetableVersionLessonsTable';

type OpenLessonsTableProps = {
  teacherUuid: string;
  qualifications: string[];
  loading: boolean;
  onAssign: (lessonUuid: string, teacherUuid: string) => void;
  onAddQualification: (teacherUuid: string) => void;
  readonly: boolean;
};

export const OpenLessonsTable = ({
  teacherUuid,
  qualifications,
  loading,
  onAssign,
  onAddQualification,
  readonly,
}: OpenLessonsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();

  const { lessons: lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const data = lessonsData.filter((l) => l.teachers.length === 0 && qualifications.includes(l.subjectUuid));

  const columns: TableColumns<LessonTableType>[] = [
    {
      header: t('lesson.table.subject'),
      id: 'subject',
      accessorKey: 'subject',
      canExpand: true,
    },
    {
      header: t('lesson.table.classesGroups'),
      id: 'groupClassNames',
      accessorKey: 'groupClassNames',
    },
    {
      header: t('deputate.title', { count: 1 }),
      id: 'deputate.titleSingluar',
      cell: ({ row }) => {
        const lessonInfo = row.original.teachingLoadInfo;
        // TODO: Work in Progress, what is contextUuid?!
        const uuid = 'contextUuid';

        // relevant deputate
        const td = lessonInfo?.deputatByTeacher.get(uuid) ?? 0;
        const cd = lessonInfo?.deputatByClass.get(uuid) ?? 0;
        const d = lessonInfo?.deputat ?? 0;
        return (td + cd + d).toFixed(2);
      },
    },
    {
      header: t('lesson.basics.isElective'),
      id: 'elective',
      accessorKey: 'elective',
      type: 'boolean',
      size: 50,
    },
    {
      header: t('lesson.basics.isTeachingLoadEnabled.short'),
      meta: {
        tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
      },
      id: 'teachingLoadEnabled',
      accessorKey: 'teachingLoadEnabled',
      type: 'boolean',
      size: 50,
    },
    {
      header: t('lesson.basics.isTimetableEnabled.short'),
      meta: {
        tooltip: t('lesson.basics.isTimetableEnabled.full'),
      },
      id: 'timetableEnabled',
      accessorKey: 'timetableEnabled',
      type: 'boolean',
      size: 50,
    },
  ];

  return (
    <Table<LessonTableType>
      loading={loading}
      className={styles['open-lessons-table']}
      showBorderRadius
      showShadow
      canScroll
      columns={columns}
      data={data}
      emptyStateSettings={
        qualifications.length === 0
          ? {
              size: 'small',
              title: '',
              subtitle: t('lesson.noQualifications'),
              padding: 'l',
              icon: <LessonIllustration />,
              buttonText: t('qualifications.add'),
              showButton: !readonly,
              onButtonClick: () => onAddQualification(teacherUuid),
            }
          : {
              size: 'small',
              title: t('lesson.noUnassigned'),
              subtitle: '',
              padding: 'l',
              icon: <LessonIllustration />,
            }
      }
      maxHeight={276}
      customHeaderHeight='var(--spacing-7)'
      customRowHeight='var(--spacing-7)'
      lastColWidth='120px'
      lastCol={({ original }) => (
        <Button
          hierarchy='tertiary-not-on-white'
          onClick={() => onAssign(original.uuid, teacherUuid)}
          disabled={readonly}
          iconPosition={'left'}
          icon={<AddIcon className={'small'} />}
        >
          {t('common.assign')}
        </Button>
      )}
    />
  );
};
